import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { BarChart3, Download, LogOut, Clock, Users, ListChecks, CalendarIcon } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import { format } from 'date-fns';
import { APP_VERSION, LAST_UPDATED } from '@/utils/version';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TASK_COLORS = {
  'Admin/Maintain X': '#3B82F6',
  'Break / Lunch': '#10B981',
  'In-Store Repair': '#F59E0B',
  'In-Store Service': '#8B5CF6',
  'On-site Repair': '#EF4444',
  'On-site Service': '#EC4899',
  'Phone Support': '#06B6D4',
  'Customer Service': '#14B8A6',
  'Refurb Repairs': '#F97316',
  'Returns / Warranties': '#84CC16',
  'Store / Demo': '#6366F1',
  'Travel': '#64748B',
  'Internal Meeting': '#8B5CF6',
  'Cleaning/Organisation': '#22D3EE',
  'Taking a Shit': '#A78BFA',
  'Custom Task': '#F472B6'
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [overviewStats, setOverviewStats] = useState(null);
  const [taskBreakdown, setTaskBreakdown] = useState([]);
  const [userStats, setUserStats] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDayBreakdown, setUserDayBreakdown] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);

  const fetchOverviewStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats/overview`);
      setOverviewStats(response.data);
    } catch (error) {
      console.error('Failed to fetch overview stats:', error);
    }
  };

  const fetchTaskBreakdown = async () => {
    try {
      const params = {};
      if (dateRange?.from && dateRange?.to) {
        params.start_date = dateRange.from.toISOString();
        params.end_date = dateRange.to.toISOString();
      }
      const response = await axios.get(`${API}/admin/stats/task-breakdown`, { params });
      setTaskBreakdown(response.data);
    } catch (error) {
      console.error('Failed to fetch task breakdown:', error);
    }
  };

  const fetchUserStats = async () => {
    try {
      const params = {};
      if (dateRange?.from && dateRange?.to) {
        params.start_date = dateRange.from.toISOString();
        params.end_date = dateRange.to.toISOString();
      }
      const response = await axios.get(`${API}/admin/stats/user-stats`, { params });
      setUserStats(response.data);
    } catch (error) {
      console.error('Failed to fetch user stats:', error);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats/recent-activity`);
      setRecentActivity(response.data);
      
      // Extract active users from recent activity (last 30 minutes)
      const thirtyMinsAgo = new Date(Date.now() - 30 * 60 * 1000);
      const active = response.data
        .filter(log => new Date(log.created_at) > thirtyMinsAgo)
        .reduce((acc, log) => {
          if (!acc.find(u => u.username === log.username)) {
            acc.push({ 
              username: log.username, 
              user_id: log.user_id,
              last_activity: log.created_at 
            });
          }
          return acc;
        }, []);
      setActiveUsers(active);
    } catch (error) {
      console.error('Failed to fetch recent activity:', error);
    }
  };

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      await Promise.all([
        fetchOverviewStats(),
        fetchTaskBreakdown(),
        fetchUserStats(),
        fetchRecentActivity()
      ]);
      setLoading(false);
    };
    fetchAllData();
  }, [dateRange]);

  const handleExportCSV = async () => {
    try {
      const params = {};
      if (dateRange?.from && dateRange?.to) {
        params.start_date = dateRange.from.toISOString();
        params.end_date = dateRange.to.toISOString();
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/export-csv?${new URLSearchParams(params)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bro-tracker-export-${format(new Date(), 'yyyy-MM-dd')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Export ready! Your data is downloading now, bro.');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    }
  };

  const fetchUserDayBreakdown = async (username) => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const response = await axios.get(`${API}/time-logs`, {
        params: {
          start_date: today.toISOString(),
          end_date: tomorrow.toISOString()
        }
      });

      // Filter for selected user and sort by time
      const userLogs = response.data
        .filter(log => log.username === username)
        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

      setUserDayBreakdown(userLogs);
    } catch (error) {
      console.error('Failed to fetch user day breakdown:', error);
    }
  };

  const handleUserClick = (username) => {
    setSelectedUser(username);
    fetchUserDayBreakdown(username);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 backdrop-blur-lg bg-white/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <div className="flex items-center gap-2 sm:gap-3">
              <img src="/logo-small.svg" alt="Bro Tracker" className="w-8 h-8 sm:w-10 sm:h-10" />
              <div>
                <h1 className="text-xl sm:text-2xl font-bold leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>Bro Tracker</h1>
                <p className="text-xs text-gray-600 hidden sm:block">Admin Dashboard</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="date-range-picker" className="h-8 sm:h-10 px-2 sm:px-3 text-xs sm:text-sm">
                    <CalendarIcon className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">
                      {dateRange?.from ? (
                        dateRange.to ? (
                          `${format(dateRange.from, 'MMM dd')} - ${format(dateRange.to, 'MMM dd')}`
                        ) : (
                          format(dateRange.from, 'MMM dd, yyyy')
                        )
                      ) : (
                        'All Time'
                      )}
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    mode="range"
                    selected={dateRange}
                    onSelect={setDateRange}
                    numberOfMonths={2}
                  />
                  {dateRange && (
                    <div className="p-3 border-t">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full"
                        onClick={() => setDateRange(null)}
                      >
                        Clear Filter
                      </Button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
              <Button
                size="sm"
                onClick={handleExportCSV}
                data-testid="admin-export-csv-button"
                className="h-8 sm:h-10 px-2 sm:px-4"
              >
                <Download className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Export CSV</span>
              </Button>
              <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-button" className="h-8 sm:h-10 px-2 sm:px-4">
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Overview Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="bg-white border border-gray-200 rounded-2xl sm:rounded-lg p-4 sm:p-6" data-testid="stat-card-hours">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-600 mb-1">Total Hours Today</p>
                <p className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
                  {overviewStats?.total_hours_today || 0}
                </p>
              </div>
              <Clock className="w-10 h-10 sm:w-12 sm:h-12 text-primary opacity-20" />
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl sm:rounded-lg p-4 sm:p-6" data-testid="stat-card-users">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-xs sm:text-sm text-gray-600 mb-1">Active Users</p>
                <p className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
                  {overviewStats?.active_users || 0}
                </p>
              </div>
              <Users className="w-10 h-10 sm:w-12 sm:h-12 text-secondary opacity-20" />
            </div>
            {activeUsers.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-xs text-gray-500 mb-2">Currently active:</p>
                <div className="flex flex-wrap gap-1">
                  {activeUsers.map((activeUser, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 text-xs rounded-full">
                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                      {activeUser.username}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl sm:rounded-lg p-4 sm:p-6" data-testid="stat-card-tasks">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-600 mb-1">Tasks Logged Today</p>
                <p className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
                  {overviewStats?.tasks_logged_today || 0}
                </p>
              </div>
              <ListChecks className="w-10 h-10 sm:w-12 sm:h-12 text-green-500 opacity-20" />
            </div>
          </div>
        </div>

        {/* Charts and Data */}
        <Tabs defaultValue="breakdown" className="space-y-4 sm:space-y-6">
          <TabsList className="w-full grid grid-cols-3 h-auto">
            <TabsTrigger value="breakdown" className="text-xs sm:text-sm py-2">Task Breakdown</TabsTrigger>
            <TabsTrigger value="users" className="text-xs sm:text-sm py-2">User Stats</TabsTrigger>
            <TabsTrigger value="activity" className="text-xs sm:text-sm py-2">Recent Activity</TabsTrigger>
          </TabsList>

          <TabsContent value="breakdown" className="space-y-4 sm:space-y-6">
            {taskBreakdown.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                {/* Pie Chart */}
                <div className="bg-white border border-gray-200 rounded-2xl sm:rounded-lg p-4 sm:p-6">
                  <h2 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                    Task Distribution
                  </h2>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={taskBreakdown}
                        dataKey="total_minutes"
                        nameKey="task"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={false}
                      >
                        {taskBreakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={TASK_COLORS[entry.task] || '#64748B'} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `${Math.round((value / 60) * 10) / 10} hours`} />
                    </PieChart>
                  </ResponsiveContainer>
                  {/* Legend */}
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {taskBreakdown.slice(0, 6).map((entry, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-xs sm:text-sm">
                        <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: TASK_COLORS[entry.task] || '#64748B' }}></div>
                        <span className="truncate">{entry.task}</span>
                        <span className="text-gray-500 ml-auto">({Math.round((entry.total_minutes / 60) * 10) / 10}h)</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Bar Chart */}
                <div className="bg-white border border-gray-200 rounded-2xl sm:rounded-lg p-4 sm:p-6">
                  <h2 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                    Time per Task
                  </h2>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={taskBreakdown}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="task" angle={-45} textAnchor="end" height={100} fontSize={12} />
                      <YAxis label={{ value: 'Hours', angle: -90, position: 'insideLeft' }} />
                      <Tooltip formatter={(value) => `${Math.round((value / 60) * 10) / 10} hours`} />
                      <Bar dataKey="total_minutes" fill="hsl(var(--primary))" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-2xl sm:rounded-lg p-8 sm:p-12 text-center">
                <BarChart3 className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-3 sm:mb-4" />
                <p className="text-sm sm:text-base text-gray-600">No task data available for the selected period</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="users">
            <div className="bg-white border border-gray-200 rounded-2xl sm:rounded-lg p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                User Performance
              </h2>
              {userStats.length > 0 ? (
                <div className="space-y-3 sm:space-y-4">
                  {userStats.map((userStat) => (
                    <div key={userStat.username}>
                      <button
                        onClick={() => handleUserClick(userStat.username)}
                        className="w-full flex items-center justify-between p-3 sm:p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
                      >
                        <div className="flex items-center gap-3 sm:gap-4">
                          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-primary font-bold text-sm sm:text-base">{userStat.username.charAt(0).toUpperCase()}</span>
                          </div>
                          <div className="text-left">
                            <p className="font-medium text-sm sm:text-base">{userStat.username}</p>
                            <p className="text-xs sm:text-sm text-gray-600">{userStat.task_count} tasks logged</p>
                          </div>
                        </div>
                        <div className="text-right flex items-center gap-2">
                          <div>
                            <p className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'var(--font-mono)' }}>
                              {Math.round((userStat.total_minutes / 60) * 10) / 10}h
                            </p>
                            <p className="text-xs text-gray-500">Click for details</p>
                          </div>
                          <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </button>

                      {/* User Day Breakdown */}
                      {selectedUser === userStat.username && userDayBreakdown.length > 0 && (
                        <div className="mt-3 p-4 bg-blue-50 rounded-xl border border-blue-200">
                          <h3 className="font-bold text-sm sm:text-base mb-3 flex items-center gap-2">
                            <span>📅</span>
                            <span>{userStat.username}&apos;s Day Breakdown</span>
                          </h3>

                          {/* Timeline Bar Visualization */}
                          <div className="mb-4 p-3 bg-white rounded-lg">
                            <p className="text-xs text-gray-600 mb-2">Time Allocation (9 AM - 5 PM)</p>
                            <div className="flex gap-0.5 h-8 rounded overflow-hidden">
                              {(() => {
                                // Create 32 blocks for 8 hours (9am-5pm) of 15-min intervals
                                const blocks = Array(32).fill(null);
                                const workStartTime = new Date();
                                workStartTime.setHours(9, 0, 0, 0);

                                userDayBreakdown.forEach(log => {
                                  const startTime = new Date(log.start_time);
                                  const minutesFromStart = (startTime.getHours() - 9) * 60 + startTime.getMinutes();
                                  const blockIndex = Math.floor(minutesFromStart / 15);
                                  
                                  if (blockIndex >= 0 && blockIndex < 32) {
                                    blocks[blockIndex] = log.task;
                                  }
                                });

                                return blocks.map((task, idx) => (
                                  <div
                                    key={idx}
                                    className="flex-1 group relative"
                                    style={{
                                      backgroundColor: task ? TASK_COLORS[task] : '#E5E7EB',
                                      minWidth: '8px'
                                    }}
                                    title={task || 'No task logged'}
                                  >
                                    {task && (
                                      <div className="hidden group-hover:block absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap z-10">
                                        {task}
                                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                                      </div>
                                    )}
                                  </div>
                                ));
                              })()}
                            </div>
                            <div className="flex justify-between text-xs text-gray-500 mt-1">
                              <span>9 AM</span>
                              <span>12 PM</span>
                              <span>5 PM</span>
                            </div>
                          </div>

                          {/* Task List */}
                          <div className="space-y-2">
                            {userDayBreakdown.map((log, idx) => (
                              <div key={idx} className="flex items-center justify-between p-2 sm:p-3 bg-white rounded-lg">
                                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                                  <div 
                                    className="w-3 h-3 rounded-full flex-shrink-0" 
                                    style={{ backgroundColor: TASK_COLORS[log.task] || '#64748B' }}
                                  ></div>
                                  <div className="min-w-0 flex-1">
                                    <p className="font-medium text-xs sm:text-sm truncate">{log.task}</p>
                                    <p className="text-xs text-gray-600">
                                      {format(new Date(log.start_time), 'HH:mm')} - {format(new Date(log.end_time), 'HH:mm')}
                                    </p>
                                  </div>
                                </div>
                                <span className="text-xs sm:text-sm font-medium text-gray-700 ml-2">{log.duration_minutes} min</span>
                              </div>
                            ))}
                          </div>
                          <div className="mt-3 pt-3 border-t border-blue-200">
                            <div className="flex justify-between text-sm">
                              <span className="font-medium">Total Today:</span>
                              <span className="font-bold">
                                {Math.round(userDayBreakdown.reduce((sum, log) => sum + log.duration_minutes, 0) / 60 * 10) / 10}h
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => setSelectedUser(null)}
                            className="mt-3 text-xs text-blue-600 hover:text-blue-800 font-medium"
                          >
                            ← Back to all users
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No user data available for the selected period</p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="activity">
            <div className="bg-white border border-gray-200 rounded-2xl sm:rounded-lg p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                Recent Activity
              </h2>
              {recentActivity.length > 0 ? (
                <div className="space-y-2 sm:space-y-3">
                  {recentActivity.map((log) => (
                    <div key={log.id} className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-xl sm:rounded-lg">
                      <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                        <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full flex-shrink-0" style={{ backgroundColor: TASK_COLORS[log.task] }}></div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm sm:text-base font-medium truncate">{log.username} - {log.task}</p>
                          <p className="text-xs sm:text-sm text-gray-600">
                            {format(new Date(log.start_time), 'MMM dd, HH:mm')} ({log.duration_minutes} min)
                          </p>
                        </div>
                      </div>
                      {log.completed_early && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded flex-shrink-0 ml-2">
                          Completed Early
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 sm:py-12">
                  <ListChecks className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-3 sm:mb-4" />
                  <p className="text-sm sm:text-base text-gray-600">No recent activity</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Footer Version Info */}
      <div className="fixed bottom-2 left-2 text-xs text-gray-500 bg-white/90 px-3 py-1.5 rounded-lg shadow-sm border border-gray-200">
        v{APP_VERSION} • Updated {LAST_UPDATED}
      </div>
    </div>
  );
};

export default AdminDashboard;
